import PropTypes from 'prop-types';
import React from 'react';
import Chart from 'react-apexcharts';
import { getMetric } from '../config/metricGlossary';
import { getBaseChartOptions, mergeChartOptions } from '../theme/apexTheme';
import { COLORS, COMPARISON_STATUS } from '../theme/tokens';
import MetricInfo from './MetricInfo';

/**
 * Headline KPI card — the "Stripe card" pattern: big tabular number, a trend
 * badge derived from the analyseit v2 comparison metrics, an optional
 * sparkline, and an optional expandable per-channel breakdown (coloured dot +
 * label + value + share bar). Replaces the single-value donuts on Overview.
 *
 * `value` is rendered as-is (pass an already-formatted string/node).
 * `trend` = { value: fraction e.g. 0.25, status: 'GAIN'|'LOSE'|'NEUTRAL' }.
 * `positiveIsGood=false` flips colour semantics (e.g. cancelled orders).
 * `breakdown` = [{ id, label, value, share: 0..1, color }] (per channel).
 */
const BreakdownRow = ({ row }) => (
  <div className="py-1">
    <div className="flex items-center gap-2 text-xs">
      <span
        className="inline-block shrink-0 rounded-full"
        style={{ width: 10, height: 10, backgroundColor: row.color }}
      />
      <span className="flex-1 truncate text-grey-darker">{row.label}</span>
      <span className="sc-tabular-nums font-medium text-black">
        {row.value}
      </span>
    </div>
    <div className="mt-1 h-1 overflow-hidden rounded-full bg-grey-light">
      <div
        className="h-1 rounded-full"
        style={{
          width: `${Math.max(2, Math.min(100, (row.share || 0) * 100))}%`,
          backgroundColor: row.color,
        }}
      />
    </div>
  </div>
);

BreakdownRow.propTypes = { row: PropTypes.object.isRequired };
const TrendBadge = ({ trend, positiveIsGood }) => {
  if (!trend || typeof trend.value !== 'number') return null;

  const { value, status } = trend;
  const isNeutral = status === COMPARISON_STATUS.NEUTRAL || value === 0;
  const isGain = status === COMPARISON_STATUS.GAIN || (!status && value > 0);

  // Direction drives the arrow; desirability (good/bad) drives the colour, so a
  // rise in a "bad" metric (e.g. cancelled orders) shows ▲ but reads red.
  const good = isGain === positiveIsGood;

  let arrow = '▼';
  if (isNeutral) arrow = '—';
  else if (isGain) arrow = '▲';

  let tone = 'bg-red-lighter text-red-dark';
  if (isNeutral) tone = 'bg-grey-light text-grey-darker';
  else if (good) tone = 'bg-green-lighter text-green-dark';

  return (
    <span
      className={`sc-tabular-nums inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${tone}`}
    >
      <span aria-hidden>{arrow}</span>
      {Math.abs(value * 100).toFixed(1)}%
    </span>
  );
};

TrendBadge.propTypes = {
  trend: PropTypes.shape({ value: PropTypes.number, status: PropTypes.string }),
  positiveIsGood: PropTypes.bool,
};

const KpiCard = ({
  metricKey,
  label,
  value,
  trend,
  positiveIsGood = true,
  sparklineData,
  sparklineColor = COLORS.primary,
  breakdown,
  open = false,
}) => {
  const metric = metricKey ? getMetric(metricKey) : null;
  const displayLabel = label || (metric && metric.label) || '';
  const hasBreakdown = open && Array.isArray(breakdown) && breakdown.length > 0;

  const sparkOptions = mergeChartOptions(
    getBaseChartOptions({ sparkline: true }),
    {
      colors: [sparklineColor],
      stroke: { width: 2 },
      tooltip: { enabled: false },
    },
  );

  return (
    <div
      className="flex flex-col justify-between rounded-lg border border-grey bg-white p-4"
      style={{ boxShadow: 'var(--sc-shadow-card)' }}
    >
      <div className="flex items-center gap-1">
        <span className="text-sm font-medium text-grey-darker">
          {displayLabel}
        </span>
        {metricKey && <MetricInfo metricKey={metricKey} />}
      </div>

      <div className="mt-2 flex items-end justify-between gap-2">
        <span className="sc-tabular-nums text-2xl font-bold leading-none text-black">
          {value}
        </span>
        <TrendBadge trend={trend} positiveIsGood={positiveIsGood} />
      </div>

      {Array.isArray(sparklineData) && sparklineData.length > 1 && (
        <div className="mt-3 -mb-1">
          <Chart
            options={sparkOptions}
            series={[{ name: displayLabel, data: sparklineData }]}
            type="line"
            height={40}
          />
        </div>
      )}

      {hasBreakdown && (
        <div className="mt-3 border-t border-grey pt-2">
          {breakdown.map(row => (
            <BreakdownRow key={row.id} row={row} />
          ))}
        </div>
      )}
    </div>
  );
};

KpiCard.propTypes = {
  metricKey: PropTypes.string,
  label: PropTypes.node,
  value: PropTypes.node.isRequired,
  trend: PropTypes.shape({ value: PropTypes.number, status: PropTypes.string }),
  positiveIsGood: PropTypes.bool,
  sparklineData: PropTypes.arrayOf(PropTypes.number),
  sparklineColor: PropTypes.string,
  breakdown: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      label: PropTypes.string,
      value: PropTypes.node,
      share: PropTypes.number,
      color: PropTypes.string,
    }),
  ),
  open: PropTypes.bool,
};

export default KpiCard;
